import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import {
  Search, X, Loader2, ShieldOff, RotateCcw, ExternalLink, ChevronLeft, ChevronRight,
} from 'lucide-react';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

const fmtUK = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export default function SuppressedTab({ apiGet, apiUnsuppress, canUnsuppress = true }) {
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [busyId, setBusyId]   = useState(null);
  const [confirm, setConfirm] = useState(null);

  const searchRef   = useRef('');
  const pageRef     = useRef(1);
  const searchInput = useRef(null);
  const debounceRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = useCallback(async (pageNum = 1) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: (pageNum - 1) * PAGE_SIZE };
      if (searchRef.current) params.search = searchRef.current;
      const data = await apiGet(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(pageNum);
      pageRef.current = pageNum;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load suppressed items');
    } finally {
      setLoading(false);
    }
  }, [apiGet]);

  useEffect(() => { load(1); }, []);

  const onSearchChange = (value) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { searchRef.current = value; load(1); }, SEARCH_DEBOUNCE_MS);
  };

  const clearSearch = () => {
    clearTimeout(debounceRef.current);
    setSearch('');
    searchRef.current = '';
    load(1);
    searchInput.current?.focus();
  };

  const doUnsuppress = async (item) => {
    setBusyId(item.output_id);
    try {
      await apiUnsuppress(item.output_id);
      toast.success(`Available again on ${item.publication_name} — generate it from Okebourne when ready`);
      await load(pageRef.current);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unsuppress');
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const askUnsuppress = (item) => setConfirm({
    title: 'Unsuppress article',
    body: `"${item.title}" is blocked on ${item.publication_name} because its story there was permanently deleted. Unsuppressing frees it for that site only. Nothing is generated or published until someone presses Generate.`,
    onConfirm: () => doUnsuppress(item),
  });

  const changePage = (p) => {
    if (p < 1 || p > totalPages) return;
    load(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const thBase = 'px-3 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap bg-slate-50 border-b border-border';
  const tdBase = 'px-3 py-3 align-middle whitespace-nowrap';

  return (
    <div className="space-y-5 pb-16" data-testid="suppressed-tab">
      <p className="text-xs text-slate-400">
        Articles blocked from regeneration on a given site because the story there was permanently deleted.Suppression is per site.
      </p>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            ref={searchInput}
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            placeholder="Search title, source URL or publication…"
            className="pl-10 pr-9 h-10 rounded-lg bg-white text-foreground border-border placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-blue-200 focus-visible:border-blue-400"
          />
          {search && (
            <button type="button" onClick={clearSearch} title="Clear search"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-foreground transition-colors">
              <X size={15} />
            </button>
          )}
        </div>
      </div>

      <p className="text-xs text-slate-400">
        {loading && items.length === 0
          ? 'Loading…'
          : total === 0
            ? 'Nothing suppressed'
            : `${total} entr${total === 1 ? 'y' : 'ies'}${total > PAGE_SIZE ? ` — page ${page} of ${totalPages}` : ''}`}
      </p>

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <ShieldOff size={28} className="mx-auto text-slate-300 mb-3" />
          <p className="text-foreground font-medium">
            {searchRef.current ? `Nothing matches "${searchRef.current}"` : 'Nothing suppressed'}
          </p>
          <p className="text-sm text-slate-400 mt-1">
            Entries appear here when a story is permanently deleted.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-border bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className={`${thBase} min-w-[280px] max-w-[420px]`}>Source article</th>
                    <th className={thBase}>Publication</th>
                    <th className={`${thBase} w-full`}>Source</th>
                    <th className={thBase}>Reason</th>
                    <th className={thBase}>Suppressed</th>
                    <th className={`${thBase} sticky right-0 z-10 border-l text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => {
                    const busy = busyId === item.output_id;
                    return (
                      <tr key={item.output_id} className="border-b border-slate-50 last:border-b-0 hover:bg-slate-50/60">
                        <td className={`${tdBase} min-w-[280px] max-w-[420px]`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <ShieldOff size={13} className="text-slate-400 shrink-0" />
                            <span className="font-medium text-foreground truncate" title={item.title}>{item.title}</span>
                          </div>
                        </td>
                        <td className={`${tdBase} text-slate-600 truncate max-w-[180px]`} title={item.publication_name}>
                          {item.publication_name}
                        </td>
                        <td className={`${tdBase} text-slate-500 truncate max-w-[180px]`}>{item.source_name || '—'}</td>
                        <td className={`${tdBase} text-slate-500 text-xs`}>{item.reason || '—'}</td>
                        <td className={`${tdBase} text-slate-600`}>{fmtUK(item.suppressed_at)}</td>
                        <td className={`${tdBase} sticky right-0 z-10 border-l border-border bg-white`}>
                          <div className="flex items-center gap-0.5 justify-end">
                            {busy ? (
                              <Loader2 size={15} className="animate-spin text-slate-400" />
                            ) : (
                              <>
                                {item.source_url && (
                                  <button title="Open source article"
                                    onClick={() => window.open(item.source_url, '_blank', 'noopener,noreferrer')}
                                    className="h-7 w-7 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 transition-colors">
                                    <ExternalLink size={15} />
                                  </button>
                                )}
                                {canUnsuppress && (
                                  <button title="Unsuppress for this publication" onClick={() => askUnsuppress(item)}
                                    className="h-7 w-7 inline-flex items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-50 transition-colors">
                                    <RotateCcw size={15} />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-4 pt-4 mt-2 border-t border-border flex-wrap">
              <p className="text-xs text-slate-400 order-2 sm:order-1">
                Showing <span className="font-medium text-slate-600">
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)}
                </span> of {total}
              </p>
              <div className="flex items-center gap-1 order-1 sm:order-2 ml-auto sm:ml-0">
                <Button variant="outline" size="sm" onClick={() => changePage(page - 1)} disabled={page <= 1}
                  className="border-border text-foreground hover:bg-slate-50 gap-1 h-8 px-3 disabled:opacity-40">
                  <ChevronLeft size={16} /> Prev
                </Button>
                <span className="px-2 text-sm text-slate-500">{page} / {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => changePage(page + 1)} disabled={page >= totalPages}
                  className="border-border text-foreground hover:bg-slate-50 gap-1 h-8 px-3 disabled:opacity-40">
                  Next <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={!!confirm} onOpenChange={o => !o && setConfirm(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">{confirm?.title}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{confirm?.body}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(null)} className="text-muted-foreground hover:text-foreground">Cancel</Button>
            <Button onClick={() => confirm?.onConfirm?.()} disabled={!!busyId} className="bg-blue-600 hover:bg-blue-700 text-white">
              {!!busyId && <Loader2 size={14} className="animate-spin mr-1.5" />}
              Unsuppress
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}